import React, { useCallback, useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import DatePicker from 'react-datepicker';
import { MdOutlineCalendarMonth } from 'react-icons/md';
import { formatImportDate, isDateValid } from '../../../utils/dates';
import TextInput from '../TextInput/TextInput';
import './styles.scss';

const updateAnchoredPopperOnResize = (reference, floating, update) => {
  window.addEventListener('resize', update);

  const resizeObserver = typeof ResizeObserver === 'undefined'
    ? null
    : new ResizeObserver(update);

  resizeObserver?.observe(reference);
  resizeObserver?.observe(floating);

  return () => {
    window.removeEventListener('resize', update);
    resizeObserver?.disconnect();
  };
};

// This replaces the defaults; flip is intentionally omitted to stop repositioning;
// +10 mirrors the library's default offset; the triangle CSS in styles.scss
// compensates for the lost arrow middleware
const anchoredPopperMiddleware = [
  {
    name: 'anchoredOffset',
    fn: ({ x, y }) => ({
      x,
      y: y + 10,
    }),
  },
];

const DateSelectorV2 = ({
  label,
  name,
  value = '',
  setValue,
  shouldDefaultDate = false,
  isLabelBeside = false,
  dateIconStyles,
  isRequired = false,
  className,
  inputStyle,
  isValid = true,
  dateFormat = 'M/d/yy',
  customLabelClasses,
  customInputClasses,
  customIcon = null,
  placeholder,
  errorMessage = undefined, // requires isRequired to be true if need to show validation error use validationError instead
  dateInputClassName = undefined,
  inputHeight,
  iconRight,
  iconBottom,
  shouldNotCapitalize = false,
  wrapperClassName = '',
  customWidth,
  validationError = undefined,
  errorMessageClass = undefined,
  errorMessageFontClasses = undefined,
  inputTextPadding = undefined,
  isAnchored = false,
  ...props
}) => {
  // State to handle local date if @value invalid or undefined
  const [localDate, setLocalDate] = useState('');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Effect used to clean the DateInput
  // when value received is a invalid date or null
  useEffect(() => {
    if (!value || !isDateValid(value)) {
      setLocalDate(null);
    } else {
      const dateFormatted = formatImportDate(value);
      setLocalDate(new Date(dateFormatted));
    }
  }, [value]);

  useEffect(() => {
    // By default, if value is empty or undefined, the component will set
    // the current date as value. However if shouldDefaultDate is false
    // the TextInput will be empty
    if (shouldDefaultDate && !isDateValid(value)) {
      const currentDate = new Date();
      setLocalDate(currentDate);
      setValue(currentDate);
    }
  }, [value, setValue, shouldDefaultDate]);

  /**
   * Set date for picker and date callback
   * @param {*} newDate date selected
   */
  const onChangedDate = useCallback(
    (newDate) => {
      if (newDate) {
        const dateFormatted = formatImportDate(newDate);
        setLocalDate(newDate);
        setValue(dateFormatted);
      } else {
        setLocalDate(null);
        setValue(null);
      }
    },
    [setValue],
  );

  const iconPosition = {
    right: iconRight !== undefined
      ? iconRight
      : 0,
    // here is Immediately Invoked Function Expression (IIFE) for bottom calculation
    // do not remove the parenthesis
    bottom: iconBottom !== undefined
      ? iconBottom
      : (() => {
        if (isLabelBeside && !errorMessage) return 2;
        if (isRequired && errorMessage && value === '') return 28;
        return 0;
      })(),
  };

  const datePicker = (
    <DatePicker
      name={name}
      showIcon
      icon={<MdOutlineCalendarMonth style={dateIconStyles || iconPosition} />}
      clearButtonClassName=""
      toggleCalendarOnIconClick
      selected={localDate}
      onChange={onChangedDate}
      dateFormat={dateFormat}
      wrapperClassName={`${customWidth || 'w-full'} ${wrapperClassName}`}
      placeholderText={placeholder}
      className={dateInputClassName}
      onCalendarOpen={() => setIsCalendarOpen(true)}
      onCalendarClose={() => setIsCalendarOpen(false)}
      popperPlacement={isAnchored ? 'bottom' : undefined}
      popperClassName={isAnchored ? 'date-selector-v2__anchored-popper' : undefined}
      popperProps={
        isAnchored
          ? {
            whileElementsMounted: updateAnchoredPopperOnResize,
            middleware: anchoredPopperMiddleware,
          }
          : undefined
      }
      customInput={(
        <TextInput
          name={name}
          label={label}
          value={localDate}
          isLabelBeside={isLabelBeside}
          isRequired={isRequired}
          className={className}
          style={{ ...inputStyle }}
          isValid={isValid}
          customLabelClasses={customLabelClasses}
          customInputClasses={customInputClasses}
          customIcon={customIcon}
          errorMessage={isCalendarOpen ? undefined : errorMessage}// requires isRequired to be true if need to show validation error use validationError instead
          inputHeight={inputHeight}
          validationError={validationError}
          errorMessageClass={errorMessageClass}
          errorMessageFontClasses={errorMessageFontClasses}
          inputTextPadding={inputTextPadding}
          {...props}
          shouldNotCapitalize={shouldNotCapitalize}
        />
      )}
    />
  );

  if (isAnchored) {
    return (
      <div className="date-selector-v2__anchored-container">
        {datePicker}
      </div>
    );
  }

  return datePicker;
};

DateSelectorV2.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string,
    PropTypes.number,
  ]),
  setValue: PropTypes.func.isRequired,
  shouldDefaultDate: PropTypes.bool,
  onKeyEnterPressed: PropTypes.func,
  isLabelBeside: PropTypes.bool,
  dateIconStyles: PropTypes.object,
  isRequired: PropTypes.bool,
  isValid: PropTypes.bool,
  inputStyle: PropTypes.object,
  dateFormat: PropTypes.string,
  className: PropTypes.string,
  customLabelClasses: PropTypes.string,
  customInputClasses: PropTypes.string,
  customIcon: PropTypes.node,
  placeholder: PropTypes.string,
  errorMessage: PropTypes.string,
  dateInputClassName: PropTypes.string,
  inputHeight: PropTypes.string,
  iconRight: PropTypes.number,
  iconBottom: PropTypes.number,
  shouldNotCapitalize: PropTypes.bool,
  wrapperClassName: PropTypes.string,
  customWidth: PropTypes.string,
  validationError: PropTypes.string,
  errorMessageClass: PropTypes.string,
  errorMessageFontClasses: PropTypes.string,
  inputTextPadding: PropTypes.string,
  isAnchored: PropTypes.bool,
};
export default DateSelectorV2;
